import { useState } from 'react';
import { GameModeNames, ServerRegionNames } from '../domain/enums';
import { useLobbyHistory } from '../queries';
import { Badge, Button, Card, Field, LoadingBlock, StateBlock, TableShell, Td, TextInput, Th } from '../ui';

type Filters = {
  match_id?: number;
  bot_id?: string;
  last?: number;
  limit?: number;
};

function formatDate(value: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/** A lobby that never launched carries match id 0, which is not an id. */
function formatId(value: string | number | undefined): string {
  const text = String(value ?? '');
  return text === '' || text === '0' ? '—' : text;
}

export default function HistoryPage() {
  const [draft, setDraft] = useState({ matchId: '', botId: '', last: '', limit: '50' });
  const [filters, setFilters] = useState<Filters>({ limit: 50 });

  const { data, isPending, isError, error, isFetching, refetch } = useLobbyHistory(filters);

  const apply = () => {
    const next: Filters = {};
    if (draft.matchId.trim()) next.match_id = Number(draft.matchId.trim());
    if (draft.botId.trim()) next.bot_id = draft.botId.trim();
    if (draft.last.trim()) next.last = Number(draft.last.trim());
    if (draft.limit.trim()) next.limit = Number(draft.limit.trim());
    setFilters(next);
  };

  const reset = () => {
    setDraft({ matchId: '', botId: '', last: '', limit: '50' });
    setFilters({ limit: 50 });
  };

  return (
    <div className="space-y-6">
      <Card
        title="Lobby history"
        description="Every lobby the API has recorded, newest first."
        actions={
          <Button size="sm" loading={isFetching} onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Match ID">
            {({ id }) => (
              <TextInput
                id={id}
                inputMode="numeric"
                value={draft.matchId}
                onChange={(event) => setDraft({ ...draft, matchId: event.target.value })}
              />
            )}
          </Field>
          <Field label="Bot ID">
            {({ id }) => (
              <TextInput
                id={id}
                value={draft.botId}
                onChange={(event) => setDraft({ ...draft, botId: event.target.value })}
              />
            )}
          </Field>
          <Field label="Last N">
            {({ id }) => (
              <TextInput
                id={id}
                inputMode="numeric"
                value={draft.last}
                onChange={(event) => setDraft({ ...draft, last: event.target.value })}
              />
            )}
          </Field>
          <Field label="Limit">
            {({ id }) => (
              <TextInput
                id={id}
                inputMode="numeric"
                value={draft.limit}
                onChange={(event) => setDraft({ ...draft, limit: event.target.value })}
              />
            )}
          </Field>
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={apply}>
              Apply
            </Button>
            <Button variant="ghost" onClick={reset}>
              Reset
            </Button>
          </div>
        </div>
      </Card>

      <Card title={`Results${data ? ` (${data.length})` : ''}`}>
        {isPending ? (
          <LoadingBlock label="Loading history…" />
        ) : isError ? (
          <StateBlock tone="bad" title="Could not load history" detail={(error as Error).message} />
        ) : data.length === 0 ? (
          <StateBlock title="Nothing recorded for those filters" />
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Created</Th>
                <Th>Lobby</Th>
                <Th>Match</Th>
                <Th>Mode</Th>
                <Th>Region</Th>
                <Th>Bot</Th>
                <Th>State</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={`${item.lobby_id}-${index}`}>
                  <Td className="whitespace-nowrap text-xs text-ink-dim">
                    {formatDate(item.created_at)}
                  </Td>
                  <Td>
                    <div className="max-w-48 truncate text-ink" title={item.lobby_name}>
                      {item.lobby_name || '—'}
                    </div>
                    <div className="font-mono text-[11px] text-ink-faint">{formatId(item.lobby_id)}</div>
                  </Td>
                  <Td className="font-mono text-xs text-ink-dim">{formatId(item.match_id)}</Td>
                  <Td className="text-xs">{GameModeNames[item.game_mode] ?? item.game_mode}</Td>
                  <Td className="text-xs">{ServerRegionNames[item.server_region] ?? item.server_region}</Td>
                  <Td className="font-mono text-[11px] text-ink-faint">{item.bot_id || '—'}</Td>
                  <Td>{item.state ? <Badge tone="neutral">{item.state}</Badge> : '—'}</Td>
                  <Td className="max-w-72">
                    <span className="line-clamp-2 text-xs text-ink-dim" title={item.notes}>
                      {item.notes || '—'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </Card>
    </div>
  );
}
